import { useEffect, useState } from "react";
import Header from "../components/Header";
import { sanityClient } from "../utils/sanityClient";

const CATEGORY_GRADIENTS = {
  Announcement: "bg-[linear-gradient(135deg,#ffd978,#ff8f6b)] text-black",
  Update: "bg-[linear-gradient(135deg,#ffb56f,#c85f47)]",
  Event: "bg-[linear-gradient(135deg,#ff8f6b,#8f3f32)]",
  "Patch Notes": "bg-[linear-gradient(135deg,#009dff,#1238ff)]",
  News: "bg-[linear-gradient(135deg,#ffd978,#ff8f6b)] text-black",
};

export default function News() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNews();
  }, []);

  async function loadNews() {
    setLoading(true);

    try {
      const query = `*[_type == "newsPost"] | order(publishedAt desc) {
        _id,
        title,
        "slug": slug.current,
        overview,
        category,
        publishedAt,
        "bannerImage": bannerImage.asset->url
      }`;

      const data = await sanityClient.fetch(query);
      setPosts(data || []);
    } catch (error) {
      console.error("Failed to load Sanity news:", error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="min-h-screen bg-[#050505] text-white">
      <Header />

      <section className="px-6 pb-24 pt-36 md:px-[7%]">
        <p className="font-black uppercase text-[#ffb56f]">Outfall News</p>

        <h1 className="outfall-heading text-[clamp(64px,10vw,130px)] leading-none text-[#ffd978] drop-shadow-[0_0_18px_#ff8f6b]">
          Latest News
        </h1>

        <p className="mt-4 max-w-2xl text-xl font-bold text-white/60">
          Updates, announcements, events, and everything happening across
          Outfall.
        </p>

        {loading ? (
          <p className="mt-10 font-bold text-white/40">Loading news...</p>
        ) : posts.length === 0 ? (
          <p className="mt-10 font-bold text-white/30">
            No posts yet — check back soon.
          </p>
        ) : (
          <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {posts.map((post) => {
              const gradient =
                CATEGORY_GRADIENTS[post.category] ||
                CATEGORY_GRADIENTS.Announcement;

              const isDark =
                post.category === "Announcement" || post.category === "News";

              return (
                <article
                  key={post._id}
                  className={`outfall-card scroll-shine relative flex min-h-[280px] flex-col justify-between overflow-hidden p-8 ${gradient}`}
                >
                  {post.bannerImage && (
                    <div
                      className="absolute inset-0 bg-cover bg-center opacity-20"
                      style={{ backgroundImage: `url(${post.bannerImage})` }}
                    />
                  )}

                  <div className="relative z-10">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-black uppercase ${
                        isDark
                          ? "bg-black/20 text-black"
                          : "bg-white/20 text-white"
                      }`}
                    >
                      {post.category || "News"}
                    </span>

                    <a href={`/news/${post.slug}`}>
                      <h2
                        className={`mt-4 text-4xl font-black leading-tight transition hover:underline ${
                          isDark ? "text-black" : "text-[#ffd978]"
                        }`}
                      >
                        {post.title}
                      </h2>
                    </a>

                    <p
                      className={`mt-3 line-clamp-4 text-base font-bold ${
                        isDark ? "text-black/80" : "text-white"
                      }`}
                    >
                      {post.overview}
                    </p>
                  </div>

                  <p
                    className={`relative z-10 mt-5 text-xs font-bold ${
                      isDark ? "text-black/50" : "text-white/50"
                    }`}
                  >
                    By Outfall Staff &middot;{" "}
                    {post.publishedAt
                      ? new Date(post.publishedAt).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : ""}
                  </p>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
